/**
 * State and mutation handlers for the protocol detail workspace.
 */

import { useCallback, useEffect, useState } from "react";
import { clinicalTrialsService } from "../../api/clinicalTrialsService";
import type {
  ProtocolAuditEvent,
  ProtocolConnection,
  ProtocolCriterion,
  ProtocolDraftBundle,
  ProtocolImportPreview,
  ProtocolTrial,
  ProtocolValidationRun,
  ProtocolVersion,
} from "../../api/clinicalTrialsApiTypes";
import { PROTOCOL_TIERS } from "../../views/ProtocolsView/ProtocolTierFilter";
import {
  acceptSuggestion,
  addDraftConnection,
  archiveToggle,
  confirmImport,
  previewImport,
  removeConnection,
  runLifecycle,
  validateDraft,
} from "./protocolDetailActions";
import { saveCriterionRationale } from "./protocolCriterionActions";
import type {
  ProtocolLifecycleAction,
  ProtocolViewMode,
} from "./protocolDetailTypes";

export type { ProtocolLifecycleAction, ProtocolViewMode };

/**
 * Loads trial/draft state and exposes mutation handlers for Protocol Detail.
 *
 * @param trialId - Site-local trial identifier from the route
 * @param enabled - Whether the hook should load (superadmin only)
 * @returns Protocol detail state and action handlers
 */
export function useProtocolDetail(trialId: string, enabled: boolean) {
  const [trial, setTrial] = useState<ProtocolTrial | null>(null);
  const [bundle, setBundle] = useState<ProtocolDraftBundle | null>(null);
  const [preview, setPreview] = useState<ProtocolImportPreview | null>(null);
  const [validation, setValidation] = useState<ProtocolValidationRun | null>(
    null,
  );
  const [suggestions, setSuggestions] = useState<ProtocolConnection[]>([]);
  const [versions, setVersions] = useState<ProtocolVersion[]>([]);
  const [audit, setAudit] = useState<ProtocolAuditEvent[]>([]);
  const [viewMode, setViewMode] = useState<ProtocolViewMode>("flow");
  const [selectedTiers, setSelectedTiers] = useState<string[]>([
    ...PROTOCOL_TIERS,
  ]);
  const [focusCriterionId, setFocusCriterionId] = useState<string | null>(null);
  const [filterText, setFilterText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadTrial = useCallback(async () => {
    if (!trialId) return;
    const nextTrial = await clinicalTrialsService.getTrial(trialId);
    setTrial(nextTrial);
    if (nextTrial.latest_draft_id) {
      const nextBundle = await clinicalTrialsService.getProtocolDraft(
        trialId,
        nextTrial.latest_draft_id,
      );
      setBundle(nextBundle);
      const suggestionResponse =
        await clinicalTrialsService.getConnectionSuggestions(
          trialId,
          nextTrial.latest_draft_id,
        );
      setSuggestions(suggestionResponse.suggestions);
    } else {
      setBundle(null);
      setSuggestions([]);
    }
    setVersions(await clinicalTrialsService.listProtocolVersions(trialId));
    const auditResponse =
      await clinicalTrialsService.listProtocolAudit(trialId);
    setAudit(auditResponse.events);
  }, [trialId]);

  useEffect(() => {
    if (!enabled || !trialId) return;
    void loadTrial().catch((err) =>
      setError(err instanceof Error ? err.message : "Failed to load trial"),
    );
  }, [enabled, trialId, loadTrial]);

  return {
    trial,
    bundle,
    preview,
    validation,
    suggestions,
    versions,
    audit,
    viewMode,
    setViewMode,
    selectedTiers,
    setSelectedTiers,
    focusCriterionId,
    setFocusCriterionId,
    filterText,
    setFilterText,
    error,
    busy,
    handlePreview: (file: File | null) =>
      previewImport(trialId, file, setBusy, setError, setPreview),
    handleConfirmImport: () =>
      confirmImport(
        trialId,
        preview,
        trial,
        setBusy,
        setError,
        setPreview,
        loadTrial,
      ),
    handleValidate: () =>
      validateDraft(
        trialId,
        trial,
        setBusy,
        setError,
        setValidation,
        loadTrial,
      ),
    handleAcceptSuggestion: (connection: ProtocolConnection) =>
      acceptSuggestion(
        trialId,
        bundle,
        connection,
        setBusy,
        setError,
        loadTrial,
      ),
    handleSaveCriterionRationale: (
      criterion: ProtocolCriterion,
      rationale: string,
    ) =>
      saveCriterionRationale(
        trialId,
        trial?.facility_id ?? "",
        bundle,
        criterion,
        rationale,
        setBusy,
        setError,
        setValidation,
        loadTrial,
      ),
    handleAddConnection: (connection: ProtocolConnection) =>
      addDraftConnection(
        trialId,
        bundle,
        connection,
        setBusy,
        setError,
        loadTrial,
        null,
      ),
    handleRemoveConnection: (connectionId: string) =>
      removeConnection(
        trialId,
        bundle,
        connectionId,
        setBusy,
        setError,
        loadTrial,
      ),
    handleLifecycle: (action: ProtocolLifecycleAction) =>
      runLifecycle(trialId, bundle, action, setBusy, setError, loadTrial),
    handleArchiveToggle: (mode: "archive" | "restore") =>
      archiveToggle(trialId, trial, mode, setBusy, setError, loadTrial),
  };
}
